import bemBlock from '@gik/core/utils/bemBlock';
import { WishlistGrid } from '@gik/wishlist-page';
import { Container, Heading, LayoutAside, RelatedArticlesSection } from '@gik/ui';
import { useSearchParam } from 'react-use';

import InkindSupportSidebarContainer from '../InkindSupportSidebar/InkindSupportSidebarContainer';
import ShareInkindButton from '../ShareInkindButton/ShareInkindButton';
import WishlistHeader from '@gik/wishlist-page/components/WishlistHeader';
import { useInkind } from '@gik/api/inkinds/inkind';
import { CalendarSection } from '@gik/calendar';
import { canUserEditPage, isInkindPageOwnerOrOrganizer } from '@gik/inkind-page/utils/inkindPagePermissions';
import { useUserStore } from '@gik/core/store/UserStore';
import { checkDonationsReminder } from '@gik/create';
import moment from 'moment';
import { checkRecipientAddressReminder } from '@gik/create/components/RecipientAddressReminder/openRecipientAddressReminder';
import isInkindPageRecipientInfoComplete from '@gik/inkind-page/utils/isInkindPageRecipientInfoComplete';
import userHasSeenInkindTourForThisPage from '@gik/inkind-page/utils/userHasSeenInkindTourForThisPage';
import { updateUserSeenPremiumPageReminders, useUser } from '@gik/api/users/user';
import useContactPageOrganizers from './useContactPageOrganizers';
import React from 'react';
import { useInkindStore } from '@gik/inkind/store/InkindStore';
import { DiscussionEmbed } from 'disqus-react';
import InkindPageInformation from '@gik/inkind-page/components/InkindPageInformation/InkindPageInformation';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/hooks/BreakpointHooks';
import { useStream } from '@gik/api/stream/useStream';
import WishlistSection from './WishlistSection';
import InkindSupportersButton from '../InkindSupportersButton/InkindSupportersButton';
import { checkUpgradePageReminder } from '../UpgradePageReminder/UpgradePageReminder';
import { optimisticUpdate } from '@gik/core/api';

const blockName = 'inkind-page-content';

export interface IInkindPageContentProps {
  inkindRouteId: string;
  isCalendarEnabled?: boolean;
  isWishlistEnabled?: boolean;
  showSuggestedItemsDisableButton?: boolean;
}

function InkindPageContent({
  inkindRouteId,
  showSuggestedItemsDisableButton,
  ...props
}: IInkindPageContentProps): JSX.Element {
  const bem = bemBlock(blockName);
  const isSmDown = useBreakpoint(Breakpoint.SM_DOWN);
  const { data: inkindPage } = useInkind(inkindRouteId);
  const setInkindRouteId = useInkindStore(state => state.setInkindRouteId);

  // TODO: move to root of app after migration
  useStream();

  React.useEffect(() => {
    setInkindRouteId(inkindRouteId);
  }, [inkindRouteId, setInkindRouteId]);

  useContactPageOrganizers(inkindRouteId);
  const userId = useUserStore(state => state.id);
  const { data: user, mutate: mutateUser } = useUser(userId);
  const isOwnerOrOrganizer = isInkindPageOwnerOrOrganizer(inkindPage, userId);
  const canEdit = canUserEditPage(inkindPage, userId);

  const inkindShareButton = (isOwnerOrOrganizer || inkindPage?.allowPageSharing) && (
    <ShareInkindButton inkindRouteId={inkindRouteId} />
  );
  const inkindSupportersButton = isOwnerOrOrganizer && <InkindSupportersButton num={inkindPage?.numSupporters || 0} />;

  const tourForceOpen = useSearchParam('tour');

  // TODO: move these to inkind page after migration
  if (inkindPage && user && canEdit && userHasSeenInkindTourForThisPage(user, inkindPage) && !tourForceOpen) {
    inkindPage.payPal &&
      !inkindPage.financialContributions.goFundMeUrl &&
      !inkindPage.financialContributions.paypalEmail &&
      checkDonationsReminder(inkindRouteId, moment.utc(inkindPage.createdUtc));

    !isInkindPageRecipientInfoComplete(inkindPage) &&
      checkRecipientAddressReminder(inkindRouteId, moment.utc(inkindPage.createdUtc));

    !inkindPage.isPremium &&
      user.seenPremiumPageReminders?.indexOf(inkindPage.routeId) < 0 &&
      checkUpgradePageReminder(moment.utc(inkindPage.createdUtc), () => {
        optimisticUpdate(
          {
            ...user,
            seenPremiumPageReminders: [...user.seenPremiumPageReminders, inkindRouteId],
          },
          mutateUser,
          () => updateUserSeenPremiumPageReminders(userId, inkindRouteId)
        );
      });
  }

  const isCalendarEnabled = props.isCalendarEnabled !== undefined ? props.isCalendarEnabled : inkindPage?.isCalendar;
  const isWishlistEnabled = props.isCalendarEnabled !== undefined ? props.isWishlistEnabled : inkindPage?.wishList;

  return (
    <div className={bem()}>
      <Container>
        <LayoutAside gutter="0" asideWidth="310px" className={bem('layout-aside')}>
          <main>
            {isSmDown && inkindSupportersButton}
            {isSmDown && inkindShareButton}

            {/* component itself has padding-x=15px, mb=30px, so here 0 margins, 0 paddings */}
            <section className={bem('inkind-page-information')}>
              <InkindPageInformation inkindRouteId={inkindRouteId} />
            </section>

            {isSmDown && <InkindSupportSidebarContainer inkindRouteId={inkindRouteId} />}

            {/* if the calendar is disabled show the wishlist regardless if it's disabled too */}
            {isCalendarEnabled ? (
              <section className={bem('calendar-container')} id="inkind-calendar">
                <CalendarSection />
              </section>
            ) : (
              <section className={bem('wishlist-page')}>
                <WishlistHeader inkindRouteId={inkindRouteId} />
                <WishlistGrid inkindRouteId={inkindRouteId} isFullWishlistPage={false} />
              </section>
            )}
          </main>
          <aside className={bem('page-content-aside')}>
            {!isSmDown && (
              <section className={bem('right-sidebar-container')}>
                {inkindSupportersButton}
                {inkindShareButton}
                <InkindSupportSidebarContainer inkindRouteId={inkindRouteId} />
              </section>
            )}
            {isCalendarEnabled && isWishlistEnabled && <WishlistSection inkindRouteId={inkindRouteId} />}
            {(inkindPage ? inkindPage.enableSuggestedArticles : true) && (
              <RelatedArticlesSection inkindRouteId={inkindRouteId} showDisable={showSuggestedItemsDisableButton} />
            )}
          </aside>
        </LayoutAside>

        {/* TODO: replace with stream comments section, coming soon... */}
        {inkindPage && (
          <section className={bem('comments')}>
            <Heading>KIND WORDS & COMMENTS</Heading>
            <DiscussionEmbed shortname="giveinkind" config={{}} />
          </section>
        )}
      </Container>
    </div>
  );
}

export default InkindPageContent;
